import React, {FC} from "react";
import {useDispatch} from "../../store";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import {useTranslation} from "next-i18next";
import {UserAvatar} from "../user-avatar";
import Fade from "@mui/material/Fade";
import {useForex} from "../../hooks/use-forex";
import Box from "@mui/material/Box";
import {DynamicTypeIcon} from "../dynamic-type-icon";
import {Compass as CompassIcon} from "../../icons/compass";
import NextLink from "next-translate-routes/link";
import {TripInterface} from "../../types/trip";
import tripImagesById from "../../generated/trip-images-by-id.json";
import tripDatabase from "../../generated/trip-database.json";
import {Image} from "../image";
import {setSelectedTripId} from "../../slices/user-session-slice";
import {BestMonths} from "./best-months";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";

interface TripResultStaticProps {
    diveTrip: TripInterface;
}


export const TripResultStatic: FC<TripResultStaticProps> = ({diveTrip}) => {


    const {t, i18n} = useTranslation();

    const tripImageFilename = tripImagesById[diveTrip.tripId as keyof typeof tripImagesById]?.[0]

    const image = tripImageFilename ? `/content/trips/${diveTrip.tripId}/img/${tripImageFilename}` : "/images/placeholder/1280x853.png"

    const {convertCurrency} = useForex();

    const formattedCurrency = convertCurrency(diveTrip.offerPrice?.USD ?? 0, "USD");

    const dispatch = useDispatch()


    const tripHrefRoot = {
        pathname: "/diving/[countrySlug]/[tripSlug]",
        query: {
            countrySlug: diveTrip.country.slug,
            tripSlug: diveTrip.slug
        }
    }

    return (
        <Paper
            sx={{
                mb: 3,
                m: {
                    xs: 1,
                    sm: 2,
                },
                pt: {
                    xs: 1,
                    sm: 2,
                    md: 2
                },
                pb: {
                    xs: 1,
                    sm: 3,
                    md: 4
                },
                px: {
                    xs: 1,
                    sm: 2,
                    md: 3
                },
                ...(Boolean(diveTrip.featured) && {
                    backgroundColor: "#feffee",
                    border: "1px solid #dadada",
                })
            }}
            elevation={diveTrip.featured ? 0 : 4}
        >
            <NextLink
                href={tripHrefRoot}
                passHref
                legacyBehavior
            >
                <Typography
                    variant='h4'
                    component='a'
                    display='block'
                    gutterBottom
                    sx={{
                        textDecoration: "none",
                        color: "black",
                        fontFamily: "Neutra Text TF",
                        textTransform: "none",
                        fontSize: {
                            xs: "1.7rem"
                        },
                        cursor: "pointer"
                    }}
                >
                    {diveTrip.title}
                </Typography>
            </NextLink>
            <Grid container spacing={3}>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={4}
                    lg={5}
                    // ref={boundaryRef}
                    style={{paddingBottom: 0, marginBottom: -12}}
                >

                    <NextLink
                        href={tripHrefRoot}
                        passHref
                        legacyBehavior
                    >
                        <a id={diveTrip.tripId} style={{position: "relative"}}
                           onClick={() => dispatch(setSelectedTripId(diveTrip.tripId))}
                        >
                            <Image src={image} width={1280} height={853}
                                   layout={"responsive"}
                                   placeholder={image.indexOf("placeholder") > 0 ? undefined : "blur"}
                                   blurDataURL={tripDatabase[diveTrip.tripId as keyof typeof tripDatabase]?.firstImageBlurDataURL || ""}
                                   sizes={"50vw"}
                            />
                            {!!diveTrip.membersOnly && (
                                <img
                                    src={`/api/members-only-overlay-image?lng=${i18n.language}&date=${diveTrip.membersOnlyDate}`}
                                    alt={"members only"}
                                    style={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        margin: "auto",
                                        width: "100%",
                                        height: "auto",
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                }}
                                />
                            )}
                        </a>
                    </NextLink>


                    {!!diveTrip.membersOnly &&
                        <Box sx={{
                            mt: {
                                xs: 2,
                                sm: 0
                            },
                            mb: {
                                xs: 7,
                                sm: 7
                            }
                        }}></Box>
                    }

                    {diveTrip.bestMonths && !diveTrip.membersOnly && (
                        <BestMonths bestMonths={diveTrip.bestMonths}/>
                    )}
                </Grid>

                <Grid item
                      xs={12}
                      sm={12}
                      md={5}
                      lg={4}
                >
                    <Grid
                        container
                        direction='column'
                        alignItems={"stretch"}
                        justifyContent={"space-between"}
                        spacing={2}
                    >
                        <Grid item xs style={{flexBasis: "auto"}}>
                            <Box
                                sx={{
                                    p: 1,
                                    borderRadius: "2px",
                                    border: "1px solid #1e1e1e",
                                    overflowX: "hidden",
                                    backgroundColor: "#F6F6F6",
                                    textAlign: "left",
                                }}
                            >
                                <CompassIcon width={40} height={40} sx={{
                                    paddingTop: "6px",
                                    fontSize: 52,
                                    float: "left",
                                    marginRight: "2px",
                                }}/>

                                <Typography sx={{
                                    marginTop: "6px",
                                    fontFamily: "Neutra Text TF",
                                    whiteSpace: "nowrap",
                                    lineHeight: "1.3rem"
                                }} variant='body1'>
                                    {diveTrip.region?.toUpperCase()}<br/>
                                    {diveTrip.country?.title?.toLowerCase()}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs style={{flexBasis: "auto"}}>
                            {/*<Typography variant="h5" component="p" sx={{fontWeight: 400}}>*/}
                            {/*    {t("why-we-love-it")}*/}
                            {/*</Typography>*/}
                            <Typography variant='body1'>
                                {diveTrip.resultDescription}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} style={{maxWidth: "100%", flexBasis: "auto"}}>
                            {!!diveTrip.interested && diveTrip.interested.length > 0 && (
                                <Fade in={true} timeout={400}>
                                    <Typography variant={"subtitle1"} sx={{fontStyle: "oblique"}}>
                                        {t("divers-interested")}:
                                    </Typography>
                                </Fade>
                            )}

                            {!!diveTrip.interested && (
                                <Box sx={{pl: 2, display: "flex", height: 72, flexWrap: "wrap", overflowY: "hidden"}}>
                                    {diveTrip.interested.map((slimUser, idx) => {
                                        return (
                                            <Fade
                                                key={idx}
                                                in={true}
                                                mountOnEnter={true}
                                                timeout={500 * (idx + 1)}
                                            >
                                                <div style={{display: "inline"}}>
                                                    <UserAvatar
                                                        slimUser={slimUser}
                                                        alternateBackgroundColor={
                                                            diveTrip.featured ? "#feffee" : undefined
                                                        }
                                                    />
                                                </div>
                                            </Fade>
                                        );
                                    })}
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item
                      xs={12}
                      sm={12}
                      md={3}
                      lg={3}
                >
                    <DynamicTypeIcon
                        diveType={diveTrip.diveType}
                        style={{float: "left", fontSize: 60}}
                    />
                    <Typography variant='h6' align={"right"} fontWeight={400}>
                        <Typography display={"inline"} fontWeight={700}
                                    color={"primary.dark"}>{diveTrip.offer}</Typography>{" "}
                        <span style={{whiteSpace: "nowrap"}}>{t("starting-from")}</span>
                    </Typography>
                    <Typography
                        textAlign={"right"}
                        display={"block"}
                        variant={"h6"}
                        component={"div"}
                        fontWeight={700}
                        fontSize={"2.8rem"}
                        color={"primary.dark"}
                        style={{
                            fontSize: "1.8rem",
                            lineHeight: "2.6rem",
                        }}
                        sx={{
                            pt:0,
                            pb:2
                        }}
                    >{formattedCurrency}</Typography>

                    <NextLink
                        href={tripHrefRoot}
                        passHref
                        legacyBehavior
                    >
                        <Button
                            component={"a"}
                            variant='contained'
                            color='secondary'
                            sx={{
                                mt: {
                                    xs: 1,
                                    lg: 3
                                },
                                whiteSpace: "nowrap",
                                fontSize: "1.4rem",
                                // lineHeight: "1.6rem",
                                px: 1,
                                textAlign: "right",
                            }}
                            fullWidth
                            style={{color: "#fff"}}
                            endIcon={<DoubleArrowIcon sx={{marginBottom: "4px"}}/>}
                            onClick={() => dispatch(setSelectedTripId(diveTrip.tripId))}
                        >
                            {t("see-details")}
                        </Button>
                    </NextLink>
                </Grid>
            </Grid>
        </Paper>
    );
}

